import { deepLinkRedirectUrlAtom } from '@capsicum/common';
import { useRouter } from '@capsicum/common-web';
import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';

export const useDeeplinkRedirectGuard = () => {
	const pathname = usePathname();
	const { replace } = useRouter();
	const [deepLinkRedirectUrl, setDeepLinkRedirectUrl] = useAtom(deepLinkRedirectUrlAtom);

	/**
	 * Returns the current pathname with the query params.
	 * @info We are not using useSearchParams for avoiding inconsistencies related to the following:
	 * https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
	 */
	const getCurrentPath = useCallback(() => {
		if (typeof window === 'undefined') return pathname;

		const params = new URLSearchParams(window.location.search);
		const paramsString = params.toString();

		return `${pathname}${paramsString ? '?' + paramsString : ''}`;
	}, [pathname]);

	/**
	 * Sets the deeplink redirect url. If no param is set the current url will be used.
	 *
	 * @param deeplink string
	 */
	const setDeeplinkRedirect = useCallback(
		(deeplink = '') => {
			const deeplinkUrl = deeplink || getCurrentPath();

			setDeepLinkRedirectUrl(deeplinkUrl);
		},
		[getCurrentPath, setDeepLinkRedirectUrl]
	);

	/**
	 * Navigates to the stored deeplink and then resets the value.
	 *
	 */
	const navigateToDeepLink = useCallback(() => {
		if (deepLinkRedirectUrl) {
			replace(deepLinkRedirectUrl);
		}

		setDeepLinkRedirectUrl(RESET);
	}, [deepLinkRedirectUrl, replace, setDeepLinkRedirectUrl]);

	return {
		deepLinkRedirect: deepLinkRedirectUrl,
		navigateToDeepLink,
		setDeeplinkRedirect
	};
};
